import { Link } from "react-router-dom";
import { Layout, Typography, theme } from "antd";
import Seo from "../common/Seo.js";
import grain from "../../customIcons/svgs/items/Grain.svg";
import flour from "../../customIcons/svgs/items/Flour.svg";
import bread from "../../customIcons/svgs/items/Bread.svg";

const { Paragraph, Title } = Typography;

export default function AboutPage() {
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();
  return (
    <>
      <Seo
        title="About the Game | Age of Companies"
        description="Discover Age of Companies, a free multiplayer browser business simulation. Build production chains, hire workers, and trade in a persistent economy."
        path="/about"
      />
      <main id="main-content" className="public-about">
        <Layout className="public-about-layout">
          <article className="public-about-content" style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}>
            <header>
              <Title>About the game</Title>
              <Paragraph>Age of Companies is a free multiplayer business simulation game played in your browser. Build a company, find your place in the economy, and grow alongside other players in a persistent world.</Paragraph>
            </header>
            <section>
              <Title level={2}>Build a business from the ground up</Title>
              <Paragraph>Buy land, build facilities, and hire workers to turn resources into products. Connect production steps into supply chains: harvest wood and saw it into planks, or grow grain, mill flour, and bake bread. Balance wages, storage space, and construction as your company expands.</Paragraph>
              <Paragraph>The world map has different biomes, resources, and local populations. Consider access to materials and employees, customer demand, player competition and transportation when choosing where to set up your business.</Paragraph>
              <figure className="about-production">
                <figcaption>
                  <strong>From field to market</strong>
                  <span>One product. A whole chain of businesses.</span>
                </figcaption>
                <ol className="about-production-steps">
                  {[
                    { image: grain, name: "Grow grain", detail: "Harvest the raw materials." },
                    { image: flour, name: "Mill flour", detail: "Turn your harvest into an ingredient." },
                    { image: bread, name: "Bake bread", detail: "Make goods for local inhabitants." },
                  ].map((step) => (
                    <li key={step.name}>
                      <div className="about-production-art"><img src={step.image} alt="" width="88" height="88" /></div>
                      <strong>{step.name}</strong>
                      <span>{step.detail}</span>
                    </li>
                  ))}
                </ol>
              </figure>
            </section>
            <section>
              <Title level={2}>Persistent multiplayer game</Title>
              <Paragraph>The game advances through cycles. Once per hour the game runs your planned production, resolves employee hiring/firing/salary, sell products to the local inhabitants and updates the game state.</Paragraph>
              <Paragraph> Trade with other players for mutual benefit, or compete for market share.</Paragraph>
            </section>
            <section>
              <Title level={2}>The world reacts to your choices</Title>
              <Paragraph>Each region has its own population of local inhabitants. Inhabitants need goods and services in order to be satisfied, which can be supplied by the companies in the area.</Paragraph>
              <Paragraph>As the needs of the inhabitants are increasingly met, they will interact differently with the world. Some examples are:</Paragraph>
              <ul>
                <li>Population size grows - Leads to more available workers and larger market</li>
                <li>Employee expected salary moves 5% toward a target based on employment rate each cycle</li>
                <li>Buying power increases as salary increases</li>
                <li>Increased demand for goods and services as population grows</li>
              </ul>
            </section>
            <section>
              <Title level={2}>Create an account to get started</Title>
              <Link className="public-about-play" to="/?auth=register">Play for free</Link>
            </section>
          </article>
        </Layout>
      </main>
    </>
  );
}
